import IconDocument from "@shared/components/icons/Document"
import IconIosCheckmarkOutline from "@shared/components/icons/IosCheckmarkOutline"
import type { CardStack, CardStackSummary } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import { tracked } from "@supergrain/kernel/react"
import type LessonEditor from "cc-frontend/services/lesson-editor"
import type Session from "cc-frontend/services/session"
import moment from "moment"
import { useRef, useState } from "react"
import { guardedSave } from "react-app/lib/guarded-save"
import { useEmberService } from "react-app/providers/ember-service-context"
import { Button, Input, TextField } from "react-aria-components"

interface Props {
  // We don't need the whole courseDate, just its cardStackId.
  // Pass in only that to avoid rerendering every DateRow whenever course.calendar changes.
  courseDateCardStackId: string | null
  cardStack: CardStackSummary | CardStack
  course: Course
  planbook: Planbook
  rotationCalendar: RotationCalendar
  dateString: string
  /** How deep this date sits inside units — drives the row's indent class. */
  unitDepth: number
  hasFocus: boolean
  canBeEditable: boolean
  isSelectable: boolean
  selectedDateStrings: Array<string>
  onLessonSelect?: (cardStackId: string | null, dateString: string) => void
  onFocusIn?: () => void
  arrowUpDown: (direction: "up" | "down") => void
}

/**
 * The Ember `DateRow`, keeping its DOM contract (classes and data attributes) — the outline's
 * focus management and the legacy drag hooks find rows by it. Everything the provider hands it,
 * and no resolution of its own.
 */
const DateRow = tracked(function DateRow({
  courseDateCardStackId,
  cardStack,
  course,
  planbook,
  rotationCalendar,
  dateString,
  unitDepth,
  hasFocus,
  canBeEditable,
  isSelectable,
  selectedDateStrings,
  onLessonSelect,
  onFocusIn,
  arrowUpDown,
}: Props) {
  const lessonEditor = useEmberService<LessonEditor>("lesson-editor")
  const session = useEmberService<Session>("session")

  // SimpleContentEditable's originalValue: an edit counts only if the text changed since focus.
  const originalTitleRef = useRef<string>("")
  // Non-null exactly while the row is being edited; the store's title shows otherwise.
  const [draft, setDraft] = useState<string | null>(null)

  const cardStackId = cardStack.id
  const shownTitle = draft ?? cardStack.attributes.title ?? ""
  const permissions = cardStack.meta?.permissions ?? []
  const isReadOnly = canBeEditable === false || !permissions.includes("can-edit-plan")
  const isSelected = selectedDateStrings?.includes(dateString) ?? false

  const models = {
    cardStack: cardStack as CardStack & { attributes: { usedAs: "lesson" } },
    dateString,
    planbook,
    course,
    rotationCalendar,
  }

  const handleChange = guardedSave((value: string) => {
    if (isReadOnly) return
    setDraft(value)
    // SimpleContentEditable's notifyValueUpdate guard: an unchanged non-empty value is not an edit.
    if (value !== "" && value === originalTitleRef.current) return
    if (session.isAuthenticated !== true) return
    lessonEditor.editTitle(models, cardStackId, value, false)
  })

  const handleBlur = guardedSave(() => {
    const value = shownTitle
    setDraft(null)
    if (isReadOnly) return
    // finishEditingTitle creates the lesson before it compares, so without this check, tabbing
    // through an untouched row creates one.
    if (value === originalTitleRef.current) return
    if (session.isAuthenticated !== true) return
    lessonEditor.finishEditingTitle(models, cardStackId, value, cardStack.attributes.title || null)
  })

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowUp") arrowUpDown("up")
    if (event.key === "ArrowDown") arrowUpDown("down")
  }

  return (
    <div
      className="js-lesson-exterior course-outline-date-wrapper"
      data-course-id={course.id}
      data-date-string={dateString}
      data-card-stack-id={cardStackId}
    >
      <div className={`course-outline-date depth-${unitDepth} ${hasFocus ? "is-focused-on" : ""}`}>
        <div
          className={`${isReadOnly ? "" : "js-lesson-interior"} course-outline-date__interior`}
          data-course-id={course.id}
          data-date-string={dateString}
          data-card-stack-id={cardStackId}
        >
          {isSelectable ? (
            <Button
              className={`course-outline-date__icon ${isSelected ? "is-selected" : ""}`}
              onPress={() => onLessonSelect?.(courseDateCardStackId, dateString)}
            >
              <IconIosCheckmarkOutline />
            </Button>
          ) : (
            <span
              className={`course-outline-date__icon js-lesson-move-pad ${
                permissions.includes("can-edit-plan") ? "js-can-drag-lesson" : ""
              }`}
            >
              <IconDocument />
            </span>
          )}
          {/* `contents` keeps the TextField wrapper out of the interior's flex layout, so the
              input stays the flex child the outline CSS sizes. */}
          <TextField
            aria-label="Lesson topic"
            isReadOnly={isReadOnly}
            value={shownTitle}
            onChange={handleChange}
            className="contents"
          >
            <Input
              className="js-course-outline-input course-outline-date__input"
              placeholder="Lesson Topic"
              data-card-stack-id={cardStackId}
              data-course-outline-input-id={dateString}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              onFocus={() => {
                originalTitleRef.current = shownTitle
                setDraft(shownTitle)
                onFocusIn?.()
              }}
            />
          </TextField>
        </div>
        <div className="course-outline-date__formatted-date">
          {moment(dateString).format("dd M/D")}
        </div>
      </div>
    </div>
  )
})

export default DateRow
